import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiErrorText, apiFetch } from "../api.js";
import { useAuth } from "../auth.js";

type Channel = "email" | "whatsapp";

/**
 * HOW WE MAY REACH YOU ABOUT THIS REQUEST — after it is open.
 *
 * Consent rows were written at submission and nowhere else, which left two
 * groups with no lawful basis and no way to give one: every case opened before
 * the contact fieldset existed, and anyone who declined at submission and then
 * changed their mind. There was a withdrawal control and no counterpart, so the
 * only permitted movement was off.
 *
 * IT SHOWS STATE, AND THAT IS NEW. `ConsentWithdrawal` renders one-way buttons
 * rather than checkboxes, and its own comment says why: nothing could READ
 * consent, so "an unticked box IS a claim that the channel is off" and no such
 * claim could be substantiated. `GET /cases/:id/consents` now answers, so this
 * control can say what is true instead of implying it — and it says nothing at
 * all until the answer arrives.
 *
 * WHAT IT DOES NOT PROMISE. Granting permission is not a subscription: it means
 * we MAY write to you about this request, not that anything is scheduled. Email
 * only sends where a channel is configured on the server, and WhatsApp cannot
 * send at all yet — the application has never collected a telephone number, so
 * the permission is real and the means is missing. Saying so is better than a
 * tick that quietly does nothing.
 */
export function CaseContactChannels({ caseId }: { caseId: string }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const key = ["case-consents", caseId];
  const { data, isPending, error } = useQuery({
    queryKey: key,
    queryFn: async () =>
      apiFetch<{ channels: string[] }>(`/cases/${caseId}/consents`, { token: await getToken() }),
  });

  const grant = useMutation({
    mutationFn: async (channel: Channel) =>
      apiFetch(`/cases/${caseId}/consents`, {
        token: await getToken(),
        method: "POST",
        body: { channel },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const withdraw = useMutation({
    mutationFn: async (channel: Channel) =>
      apiFetch(`/consents/revoke`, {
        token: await getToken(),
        method: "POST",
        body: { purpose: "delivery_notification", channel, scope: { caseId } },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  // Deliberately renders nothing rather than guessing. A control that showed
  // "off" while it was still asking would be making the claim it exists to stop.
  if (isPending) return <p className="muted"><small>Checking your contact permissions…</small></p>;
  if (error) {
    return (
      <p role="alert">
        {apiErrorText(error, "Could not read your contact permissions for this request.")}
      </p>
    );
  }

  const live = new Set(data?.channels ?? []);
  const busy = grant.isPending || withdraw.isPending;

  return (
    <div className="contact-channels">
      <p className="muted">
        <small>
          Permission to write to you about <em>this</em> request, and only this one. You can
          change it whenever you like, and withdrawing takes effect immediately.
        </small>
      </p>

      <ul className="channel-list">
        {(["email", "whatsapp"] as const).map((channel) => {
          const on = live.has(channel);
          return (
            <li key={channel}>
              <span className="channel-name">
                {channel === "email" ? "Email" : "WhatsApp"}
                {on ? <strong> — permitted</strong> : <span className="muted"> — not permitted</span>}
              </span>
              <button
                type="button"
                className="quiet compact"
                disabled={busy}
                onClick={() => (on ? withdraw.mutate(channel) : grant.mutate(channel))}
              >
                {on ? "Withdraw" : "Allow"}
              </button>
              {channel === "whatsapp" ? (
                <small className="muted">
                  Allowing this permits your number and our messages to travel through
                  Meta&rsquo;s servers, outside Sri Lanka, under their rules rather than ours.
                  Nothing about your land would go in a message. Note there is still nowhere in
                  this application to give us a telephone number, so this grants a permission
                  before there is a number to use it on.
                </small>
              ) : (
                <small className="muted">
                  A message would say only that there is an update, with a link back to this
                  page. Your documents and what the attorney found stay here.
                </small>
              )}
            </li>
          );
        })}
      </ul>

      {grant.error || withdraw.error ? (
        <p role="alert">
          {apiErrorText(grant.error ?? withdraw.error, "That did not save.")}
        </p>
      ) : null}
    </div>
  );
}
